import settings from "../../../constants/settings";
import { Part, PlacementType } from "../../../reducers/models/parts";
import {
  Container,
  InventoryResponse,
} from "../../../reducers/componentSearch";
import useSWR from "swr";
import useSWRFetcher from "../commonly-stored/fetcher";
import { useContext, useMemo, useState } from "react";
import { useAppSelector } from "../store";
import { usePartsWithInventory } from "../commonly-stored/parts";
import useFetchAndTrace from "../fetchWithTrace";
import {
  BoardIdContext,
  BoardShipmentIds,
} from "../commonly-stored/uploaded-board/state-machine/reviewBoardFlowLoader";

export interface PartWithInventory {
  required_amount: number;
  num_elements_per_shipment: number;
  num_elements: number;
  part: Part;
  inventory_response: InventoryResponse;
  placement_type: PlacementType | null;
  is_consignment: boolean;
  element_names: string[];
  amount_allocated: number;
  amount_spent: number;
}

export interface BoardPartsResponse {
  parts: PartWithInventory[];
}

function amountAllocated(containers: Container[], shipmentId: string): number {
  let total = 0;
  containers.forEach((container) => {
    container.part_allocations.forEach((allocation) => {
      if (allocation.shipment_id === shipmentId) {
        total += allocation.quantity_allocated;
      }
    });
  });
  return total;
}

/**
 * What a shipment needs of each of its parts, alongside where those parts are
 * and what is set aside for it. The shipment says what is needed; everything
 * about allocations comes from the parts, which arrive and stay current over
 * the parts websocket. Nothing here needs refreshing after an edit.
 */
export default function useBoardInventory(
  boardId: string | null,
  shipmentId: string | null,
) {
  const shipment = useAppSelector((state) => {
    if (boardId === null || shipmentId === null) {
      return undefined;
    }
    return state.uploadedBoard.boards[boardId]?.shipments[shipmentId];
  });
  const partIds = useMemo(() => {
    return (shipment?.shipment_parts ?? []).map((shipmentPart) => {
      return shipmentPart.part_id;
    });
  }, [shipment]);
  const { parts, loading } = usePartsWithInventory(partIds);

  const boardPartsResponse = useMemo((): BoardPartsResponse | null => {
    if (shipment === undefined || shipmentId === null || loading) {
      return null;
    }
    const partsWithInventory: PartWithInventory[] = [];
    shipment.shipment_parts.forEach((shipmentPart) => {
      const partAndInventory = parts.get(shipmentPart.part_id);
      if (partAndInventory === undefined) {
        return;
      }
      const physicalInventory = partAndInventory.inventory.physical_inventory;
      partsWithInventory.push({
        required_amount: shipmentPart.required_amount,
        num_elements_per_shipment: shipmentPart.num_elements_per_shipment,
        num_elements: shipmentPart.num_elements,
        part: partAndInventory.part,
        inventory_response: partAndInventory.inventory,
        placement_type: shipmentPart.placement_type,
        is_consignment: shipmentPart.is_consignment,
        element_names: shipmentPart.element_names,
        amount_allocated: amountAllocated(
          physicalInventory.containers,
          shipmentId,
        ),
        amount_spent: physicalInventory.amounts_spent[shipmentId] ?? 0,
      });
    });
    return { parts: partsWithInventory };
  }, [shipment, shipmentId, loading, parts]);

  return { boardPartsResponse };
}

export function usePartPlacementType(
  boardId: string,
  onSuccess?: () => void,
  onError?: (detail: string) => void,
) {
  const { shipmentId } = useContext(BoardIdContext) as BoardShipmentIds;
  const fetchAndTrace = useFetchAndTrace();
  const [loading, setLoading] = useState<boolean>(false);
  const { refreshContainersNeededOnFeeders } =
    useContainersNotOnFeedersForBoard(boardId, shipmentId);

  const updatePlacementHandler = (response: Response) => {
    setLoading(false);
    if (response.ok) {
      onSuccess?.();
      void refreshContainersNeededOnFeeders();
    } else {
      throw Error("Failed to update part placement type.");
    }
  };

  const errorHandler = () => {
    onError?.("Failed to update part placement type.");
    setLoading(false);
  };

  const updatePartPlacementType = (
    partId: string | null,
    placementType: string,
    updatePart: boolean,
  ) => {
    if (partId !== null) {
      setLoading(true);
      void fetchAndTrace(
        `${settings.api_url}/boards/${boardId}/placement-type`,
        updatePlacementHandler,
        errorHandler,
        {
          method: "PUT",
          body: JSON.stringify({
            part_id: partId,
            placement_type: placementType,
            update_part: updatePart,
          }),
        },
      );
    }
  };

  return {
    updatePartPlacementType,
    loading,
  };
}

export function useUpdateReelDetails(partId: string | null) {
  const fetchAndTrace = useFetchAndTrace();
  const [loading, setLoading] = useState<boolean>(false);

  const updateReelDetailsHandler = (response: Response) => {
    setLoading(false);
    if (response.ok) {
    } else {
      throw new Error("Failed to update reel details for this part.");
    }
  };

  const errorHandler = () => {};

  const updateReelDetails = (width: number | null, pitch: number | null) => {
    if (partId === null) {
      return;
    }
    setLoading(true);
    void fetchAndTrace(
      `${settings.api_url}/parts/${partId}/feeder-details`,
      updateReelDetailsHandler,
      errorHandler,
      {
        method: "PUT",
        body: JSON.stringify({ reel_width_mm: width, reel_pitch_mm: pitch }),
      },
    );
  };

  return {
    updateReelDetails,
    loading,
  };
}

export interface FeederStatus {
  containers_on_feeders: Container[];
  containers_in_board_tote: Container[];
  containers_not_on_feeders_or_in_tote: Container[];
  parts_not_smt_ready: string[];
  feeders_still_needed: Record<string, number>;
}

export interface FeederStatusResponse {
  top: FeederStatus;
  bottom: FeederStatus;
}

export function useContainersNotOnFeedersForBoard(
  boardId: string,
  shipmentId: string | null,
) {
  const fetcher = useSWRFetcher<FeederStatusResponse>();
  const { data, mutate, isValidating } = useSWR<FeederStatusResponse>(
    shipmentId
      ? `${settings.api_url}/inventory/containers/feeder-status/${boardId}/shipments/${shipmentId}`
      : null,
    fetcher,
  );

  return {
    containersNeededOnFeeders: data === undefined ? null : data,
    loading: isValidating,
    refreshContainersNeededOnFeeders: mutate,
  };
}
